/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Cup Customizer Page - Single Page Design Experience
 * Updated: 2026-01-09
 *
 * Fresh implementation for Cup customization:
 * - Single-page layout (all controls on one screen)
 * - Firestore-first save (image upload BEFORE confirm)
 * - Async billing processing
 * - Focus on Haravan integration
 * - Notification integration
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ChevronLeft } from 'lucide-react';
import logo from '../data/banner/logo-xoa-background.png';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { modalApi } from '../services/modalApi';
import { imageUrlService } from '../services/imageUrlService';
import { saveDesignToFirestore, validateDesignData } from '../services/cupCustomizerService';
import { generateFinalMockupWithPosition } from '../services/cupCustomizerMockup';
import { applyLaserTransform, applySilkTransform, applyBlackPrintTransform } from '../services/ImageColorTransform';
import SinglePageLayout from '../components/CupCustomizer/SinglePageLayout';
import CompletionPage from '../components/CompletionPage';
import ImageProcessingOverlay from '../components/ImageProcessingOverlay';
import MaintenancePage from '../components/MaintenancePage';
import { useNotification } from '../components/Notification/NotificationProvider';
import { useTemplates } from '../hooks/useTemplates';
import '../services/cupDetectionTest'; // Import test tool (available in console)
import '../services/cupDetectionDebug'; // Import debug tool for threshold testing
import '../services/smartThresholdTest'; // Import smart threshold test utility

interface DesignOptions {
  // Image files (MUST be uploaded to Modal before confirm)
  imageBase64: string;
  imageId: string;
  imagePath: string;

  // Transformed image (for laser engraving)
  transformedImageBase64?: string;
  transformedImageId?: string;
  transformedImagePath?: string;

  // Preview/Mockup
  mockupPath: string;

  // Design parameters
  position: { x: number; y: number }; // Position in % relative to center (0,0 = center, -10,15 = left 10%, up 15%)
  scale: number; // Scale in % relative to printable area
  rotation: number;
  printType: 'laser' | 'silk';
  color: string;
  customPrompt?: string;
  detailThreshold?: number; // Laser detail threshold (0-255, default 50 - median-based)

  // Template selection
  templateId: string;
}

const CupCustomizerPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const { showError } = useNotification();

  // UI State
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [loadingProgress, setLoadingProgress] = useState<number>(0);

  // Maintenance Mode State
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);
  const [lowBalance, setLowBalance] = useState(false);
  const [checkingBalance, setCheckingBalance] = useState(true);

  // Completion State
  const [completionData, setCompletionData] = useState<{
    designId: string;
    mockupUrl: string;
    templateName: string;
    variantId: number;
  } | null>(null);

  // Design State
  const [designOptions, setDesignOptions] = useState<Partial<DesignOptions>>({
    position: { x: 0, y: 0 },
    scale: 100,
    rotation: 0,
    printType: 'laser',
    color: 'gold',
    detailThreshold: 50,  // Changed from 100 to 50 (median-based, more aggressive detail)
    imageBase64: '',
    imageId: '',
    imagePath: '',
    mockupPath: '',
    templateId: '',
  });

  // Template State with Real-time Listener
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const { templates, loading: templatesLoading, error: templatesError } = useTemplates('coffee_cup', false); // Real-time listener

  // Visitor/Session ID
  const [visitorId, setVisitorId] = useState('');
  const [sessionId, setSessionId] = useState('');

  // Check maintenance mode (real-time listener)
  useEffect(() => {
    console.log('[CupCustomizer] Setting up maintenance mode listener...');

    const unsubscribe = onSnapshot(
      doc(db, 'system_settings', 'maintenance_mode'),
      (docSnap) => {
        if (docSnap.exists()) {
          const enabled = docSnap.data().enabled || false;
          console.log('[CupCustomizer] Maintenance mode status:', enabled);
          setMaintenanceMode(enabled);
        } else {
          console.log('[CupCustomizer] No maintenance mode document, defaulting to false');
          setMaintenanceMode(false);
        }
        setCheckingMaintenance(false);
      },
      (error) => {
        console.error('[CupCustomizer] Error checking maintenance mode:', error);
        // On error, allow access (fail open)
        setMaintenanceMode(false);
        setCheckingMaintenance(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Check balance (auto-enable maintenance if balance < 1200)
  useEffect(() => {
    console.log('[CupCustomizer] Checking billing balance...');

    const checkBalance = async () => {
      try {
        const MODAL_API_URL = import.meta.env.VITE_MODAL_API_BASE_URL || '';
        const response = await fetch(`${MODAL_API_URL}/admin/billing/balance`);
        if (!response.ok) {
          throw new Error('Failed to fetch balance');
        }

        const data = await response.json();
        const currentBalance = data.balance || 0;

        console.log('[CupCustomizer] Current balance:', currentBalance);

        // If balance < 1200, enable maintenance mode
        const LOW_BALANCE_THRESHOLD = 1200;
        if (currentBalance < LOW_BALANCE_THRESHOLD) {
          console.warn(`⚠️ [CupCustomizer] Low balance detected (${currentBalance} < ${LOW_BALANCE_THRESHOLD}). Enabling maintenance mode.`);
          setLowBalance(true);
        } else {
          console.log(`✅ [CupCustomizer] Balance sufficient (${currentBalance} >= ${LOW_BALANCE_THRESHOLD})`);
          setLowBalance(false);
        }
      } catch (error) {
        console.error('[CupCustomizer] Error checking balance:', error);
        // On error, allow access (fail open)
        setLowBalance(false);
      } finally {
        setCheckingBalance(false);
      }
    };

    checkBalance();

    // Re-check balance every 60 seconds
    const intervalId = setInterval(checkBalance, 60000);

    return () => clearInterval(intervalId);
  }, []);

  // Initialize visitor/session IDs on mount
  useEffect(() => {
    let vid = localStorage.getItem('veritas_visitor_id');
    if (!vid) {
      vid = crypto.randomUUID();
      localStorage.setItem('veritas_visitor_id', vid);
    }
    setVisitorId(vid);

    let sid = localStorage.getItem('veritas_session_id');
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem('veritas_session_id', sid);
    }
    setSessionId(sid);
  }, []);

  // Track generated fallback masks (cache to avoid regenerating)
  const [fallbackMasks, setFallbackMasks] = useState<Record<string, string>>({});

  // Generate fallback mask for templates without mask image
  const ensureMaskUrl = async (template: any): Promise<string> => {
    if (!template?.maskUrl) return '';

    // If already a valid URL or fallback generated, return it
    if (template.maskUrl !== 'GENERATE_FALLBACK_MASK') {
      return template.maskUrl;
    }

    // Check if already generated
    if (fallbackMasks[template.id]) {
      console.log(`✅ Using cached fallback mask for template ${template.id}`);
      return fallbackMasks[template.id];
    }

    // Generate fallback mask
    console.log(`⚠️ Generating fallback mask for template ${template.id} (missing CDN mask image)`);
    try {
      const fallbackUrl = await imageUrlService.generateFallbackMaskImage();
      setFallbackMasks(prev => ({
        ...prev,
        [template.id]: fallbackUrl
      }));
      return fallbackUrl;
    } catch (err) {
      console.error('Failed to generate fallback mask:', err);
      return '';
    }
  };

  // Update selectedTemplate when templates change (real-time)
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplate) {
      // Only set default template if none selected yet
      setSelectedTemplate(templates[0]);
      setDesignOptions(prev => ({ ...prev, templateId: templates[0].id }));
    } else if (templates.length > 0 && selectedTemplate) {
      // Check if currently selected template still exists
      const stillExists = templates.find(t => t.id === selectedTemplate.id);
      if (!stillExists) {
        // Template was deleted, switch to first available
        console.warn('⚠️ Selected template no longer available, switching to first template');
        setSelectedTemplate(templates[0]);
        setDesignOptions(prev => ({ ...prev, templateId: templates[0].id }));
      } else {
        // Update selectedTemplate in case URLs changed
        const updated = templates.find(t => t.id === selectedTemplate.id);
        if (updated) {
          setSelectedTemplate(updated);
        }
      }
    } else if (templates.length === 0 && !templatesLoading) {
      // No templates available AND finished loading (not just loading delay)
      console.error('❌ No templates available after loading complete');
      setSelectedTemplate(null);
      if (templatesError) {
        showError(`Lỗi tải mẫu: ${templatesError}`);
      } else {
        showError('Không có mẫu cốc nào có sẵn. Vui lòng thử lại sau.');
      }
    }
  }, [templates, templatesLoading, templatesError]);

  // Generate fallback mask if selectedTemplate needs it
  useEffect(() => {
    if (!selectedTemplate || selectedTemplate.maskUrl !== 'GENERATE_FALLBACK_MASK') {
      return;
    }

    const generateMask = async () => {
      const fallbackUrl = await ensureMaskUrl(selectedTemplate);
      // Update selectedTemplate with generated fallback mask
      setSelectedTemplate(prev => prev ? { ...prev, maskUrl: fallbackUrl } : null);
    };

    generateMask();
  }, [selectedTemplate?.id]);

  // Expose template data for debugging
  useEffect(() => {
    if (typeof window !== 'undefined' && selectedTemplate) {
      (window as any).debugTemplate = selectedTemplate;
      console.log('🔍 [DEBUG] Current template:', selectedTemplate);
    }
  }, [selectedTemplate]);

  const canConfirm = (): boolean => {
    return !!(
      (designOptions.imageBase64 || designOptions.imagePath) &&
      selectedTemplate?.id &&
      selectedTemplate?.haravan_variant_id &&
      (selectedTemplate?.cleanUrl || selectedTemplate?.url) &&
      selectedTemplate?.maskUrl
    );
  };

  const confirmDesign = async () => {
    if (!canConfirm()) {
      showError('Please complete all steps before confirming');
      return;
    }

    setLoading(true);
    setLoadingProgress(0);
    setLoadingMessage('Đang khởi tạo...');

    try {
      // STEP 1: GENERATE FINAL MOCKUP
      setLoadingProgress(20);
      setLoadingMessage('Đang tạo hình ảnh mô phỏng...');

      // Apply color transformation based on print type
      let transformedLogo = designOptions.imageBase64;
      const detailThreshold = designOptions.detailThreshold || 50;
      if (designOptions.printType === 'laser') {
        // applyLaserTransform: (imageBase64, whiteThreshold = 220, detailThreshold = 50)
        transformedLogo = await applyLaserTransform(designOptions.imageBase64, 220, detailThreshold);
      } else if (designOptions.printType === 'silk') {
        transformedLogo = await applySilkTransform(designOptions.imageBase64);
      }

      // Validate and prepare template URLs
      // Priority: cleanUrl > url (cleanUrl is preferred for better quality)
      let cupUrl = selectedTemplate.cleanUrl || selectedTemplate.url;
      const maskUrl = selectedTemplate.maskUrl;

      if (!cupUrl) {
        throw new Error('Template cup image URL is missing. Please select another template.');
      }
      if (!maskUrl) {
        throw new Error('Template mask image URL is missing. Please select another template.');
      }

      console.log('🔍 [confirmDesign] Using template:', selectedTemplate.id);
      console.log('   Cup URL (cleanUrl):', selectedTemplate.cleanUrl);
      console.log('   Cup URL (url):', selectedTemplate.url);
      console.log('   Cup URL (selected):', cupUrl);
      console.log('   Mask URL:', maskUrl);

      // Generate final mockup with current position and scale
      // If cleanUrl fails, automatically retry with regular url
      let finalMockup: string;
      try {
        finalMockup = await generateFinalMockupWithPosition(
          cupUrl,
          maskUrl,
          transformedLogo,
          designOptions.position || { x: 0, y: 0 },
          designOptions.scale || 100,
          detailThreshold
        );
      } catch (cleanUrlError: any) {
        // If using cleanUrl and it failed, retry with regular url
        if (selectedTemplate.cleanUrl && selectedTemplate.url && cupUrl === selectedTemplate.cleanUrl) {
          console.warn('⚠️ [confirmDesign] cleanUrl failed, retrying with regular url');
          console.warn('   Error:', cleanUrlError.message);
          cupUrl = selectedTemplate.url;
          finalMockup = await generateFinalMockupWithPosition(
            cupUrl,
            maskUrl,
            transformedLogo,
            designOptions.position || { x: 0, y: 0 },
            designOptions.scale || 100,
            detailThreshold
          );
        } else {
          throw cleanUrlError;
        }
      }

      console.log('✅ Final mockup generated');

      // STEP 2: UPLOAD ORIGINAL IMAGE TO MODAL
      setLoadingProgress(40);
      setLoadingMessage('Đang tải ảnh gốc lên server...');

      let imagePath = designOptions.imagePath;
      let imageId = designOptions.imageId;

      // If image not yet uploaded, upload NOW before proceeding
      if (!imagePath) {
        if (!designOptions.imageBase64) {
          throw new Error('No image to upload. Please select an image first.');
        }

        const uploadResp = await modalApi.uploadImage(
          designOptions.imageBase64,
          visitorId,
          sessionId,
          'original'
        );

        if (!uploadResp.success) {
          throw new Error(`Image upload failed: ${uploadResp.message}`);
        }

        imagePath = uploadResp.filepath;
        imageId = uploadResp.image_id;

        console.log('✅ Original image uploaded to Modal:', imagePath);
      }

      // STEP 3: UPLOAD TRANSFORMED IMAGE FOR PRINTING
      // NOTE: Mockup uses gold/silver (transformedLogo) for beautiful preview
      // BUT: For factory printing, we convert to PURE BLACK RGB(0,0,0)
      // IMPORTANT: Apply same detailThreshold to match preview!
      setLoadingProgress(55);
      setLoadingMessage('Đang xử lý hình ảnh in...');

      let transformedImagePath = '';
      let transformedImageId = '';

      // Convert to BLACK for factory printing (both laser & silk)
      // Factory requirement: Pure black RGB(0,0,0), no gradient, no color
      // Apply same detailThreshold as preview for consistency
      console.log('🎨 [CupCustomizer] Converting to black for factory printing with detailThreshold...');
      const blackPrintLogo = await applyBlackPrintTransform(designOptions.imageBase64, detailThreshold);

      // Upload the BLACK version for factory
      const transformedUploadResp = await modalApi.uploadImage(
        blackPrintLogo.split(',')[1], // Remove data:image/png;base64, prefix
        visitorId,
        sessionId,
        'transformed'
      );

      if (!transformedUploadResp.success) {
        throw new Error(`Black print image upload failed: ${transformedUploadResp.message}`);
      }

      transformedImagePath = transformedUploadResp.filepath;
      transformedImageId = transformedUploadResp.image_id;

      console.log('✅ Black print image (RGB 0,0,0) uploaded to Modal:', transformedImagePath);
      console.log('   Print type:', designOptions.printType);
      console.log('   Preview color:', designOptions.color);
      console.log('   Detail threshold applied:', detailThreshold);
      console.log('   Factory will receive: Pure black (0,0,0) with detailThreshold=', detailThreshold);

      // STEP 4: UPLOAD MOCKUP TO MODAL
      setLoadingProgress(70);
      setLoadingMessage('Đang tải ảnh mô phỏng...');

      const mockupUploadResp = await modalApi.uploadImage(
        finalMockup.split(',')[1], // Remove data:image/png;base64, prefix
        visitorId,
        sessionId,
        'mockup'
      );

      if (!mockupUploadResp.success) {
        throw new Error(`Mockup upload failed: ${mockupUploadResp.message}`);
      }

      const mockupPath = mockupUploadResp.filepath;
      console.log('✅ Mockup uploaded to Modal:', mockupPath);

      // STEP 4.5: PROCESS BILLING (Non-blocking / Independent)
      // Fire-and-forget: Attempt to charge but don't wait for it
      // Backend validates credit > 1200d
      modalApi.chargeCupMockup({
        image_id: imageId,
        template_id: selectedTemplate.id,
        visitor_id: visitorId,
        session_id: sessionId,
        mockup_path: mockupPath
      }).catch(err => {
        console.error('⚠️ Background billing charge failed:', err);
      });

      // STEP 5: SAVE TO FIRESTORE with detailed design parameters
      setLoadingProgress(90);
      setLoadingMessage('Đang lưu thông tin thiết kế...');

      // Calculate position as % relative to center (0,0 = center)
      // Position is already in percentage format from Canvas component
      const positionPercent = {
        x: designOptions.position?.x || 0, // Horizontal offset in %
        y: designOptions.position?.y || 0  // Vertical offset in %
      };

      // Scale is already in percentage (100 = original size)
      const scalePercent = designOptions.scale || 100;

      // Validate before saving
      const designDataForValidation = {
        // Original image (always saved)
        imageId: imageId,
        imagePath: imagePath,

        // Transformed image (for printing: laser = transformed, silk = original)
        transformedImageId: transformedImageId,
        transformedImagePath: transformedImagePath,

        // Mockup preview
        mockupPath: mockupPath,

        // Template reference
        templateId: selectedTemplate.id,
        haravan_variant_id: selectedTemplate.haravan_variant_id,

        // Session tracking
        visitor_id: visitorId,
        session_id: sessionId,

        // Design parameters (all in percentage for consistency)
        position: positionPercent, // Position offset from center in %
        scale: scalePercent,       // Size relative to printable area in %
        rotation: designOptions.rotation || 0,

        // Print specifications
        printType: designOptions.printType || 'laser',
        color: designOptions.color || 'gold',
        detailThreshold: detailThreshold, // Detail threshold for laser engraving (0-255)

        // Metadata for reference
        metadata: {
          description: `${designOptions.printType === 'laser' ? 'Laser engraving' : 'Silk screen printing'} - Pure Black RGB(0,0,0) for factory - Detail threshold: ${detailThreshold}`,
          positionDescription: positionPercent.x === 0 && positionPercent.y === 0
            ? 'Center position'
            : `Offset: ${positionPercent.x > 0 ? 'right' : 'left'} ${Math.abs(positionPercent.x)}%, ${positionPercent.y > 0 ? 'down' : 'up'} ${Math.abs(positionPercent.y)}%`,
          scaleDescription: `${scalePercent}% of printable area`,
          detailThresholdDescription: `Detail threshold: ${detailThreshold} (0-255, lower = more detail)`,
          imageUsed: 'Black print version (RGB 0,0,0) - Factory ready',
          previewColor: designOptions.color, // Gold/silver for UI preview only
          factoryColor: 'black', // Always black for actual printing
          thresholdApplied: detailThreshold // Detail threshold value applied to black print version
        }
      };

      validateDesignData(designDataForValidation);

      const designId = await saveDesignToFirestore({
        ...designDataForValidation,
        haravan_product_id: selectedTemplate.haravan_product_id,
      });

      console.log('✅ Design saved to Firestore:', designId);
      console.log('📊 Design parameters:', {
        printType: designOptions.printType,
        detailThreshold: detailThreshold,
        imageType: 'Pure Black RGB(0,0,0) - Factory ready',
        previewColor: designOptions.color,
        factoryColor: 'black',
        position: positionPercent,
        scale: scalePercent,
        blackPrintImagePath: transformedImagePath,
        originalImagePath: imagePath
      });
      console.log(`✅ Detail threshold saved: ${detailThreshold} (same as preview)`);

      setLoadingProgress(100);
      setLoadingMessage('Hoàn tất!');

      // STEP 6: Show completion page with variant ID for add to cart
      // Small delay to let user see 100%
      setTimeout(() => {
        setCompletionData({
          designId,
          mockupUrl: finalMockup, // base64 mockup image
          templateName: selectedTemplate.name,
          variantId: selectedTemplate.haravan_variant_id!
        });
      }, 500);

    } catch (err: any) {
      console.error('❌ Confirm failed:', err);

      // Provide helpful error messages based on error type
      let userMessage = 'Failed to save design. Please try again.';

      if (err.message?.includes('Failed to load')) {
        userMessage = 'Không thể tải ảnh mẫu cốc. Vui lòng chọn mẫu khác hoặc thử lại.';
      } else if (err.message?.includes('URL is missing')) {
        userMessage = 'Mẫu cốc này thiếu thông tin. Vui lòng chọn mẫu khác.';
      } else if (err.message) {
        userMessage = err.message;
      }

      showError(userMessage);
      setLoading(false);
    }
  };

  // Show loading while checking maintenance mode and balance
  if (checkingMaintenance || checkingBalance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">Đang kiểm tra trạng thái hệ thống...</div>
      </div>
    );
  }

  // If maintenance mode is enabled OR balance is low, show maintenance page
  if (maintenanceMode || lowBalance) {
    return <MaintenancePage redirectPath="/" countdown={5} />;
  }

  // If completion data is set, show CompletionPage (NO height constraints for scrolling)
  if (completionData) {
    return (
      <div className="min-h-screen">
        <CompletionPage
          designInfo={{
            orderId: completionData.designId,
            productType: 'coffee_cup',
            productName: completionData.templateName,
            mockupImageUrl: completionData.mockupUrl,
            designOptions: {
              printType: designOptions.printType || 'laser',
              scale: designOptions.scale || 100,
              position: designOptions.position || { x: 0, y: 0 }
            }
          }}
          variantId={completionData.variantId}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col overflow-auto">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-1 flex items-center justify-between min-h-[64px]">
          {/* Left: Back & Logo */}
          <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Quay lại"
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
            <a
              href="/"
              className="flex items-center h-14 sm:h-20 overflow-visible py-1 cursor-pointer transition-transform hover:scale-105 active:scale-95"
            >
              <img
                src={logo}
                alt="AirX"
                className="h-20 sm:h-32 w-auto object-contain transform -translate-y-1 drop-shadow-md"
              />
            </a>
          </div>

          {/* Center: Title */}
          <h1 className="text-xs sm:text-base lg:text-lg font-extrabold text-gray-900 mx-1 text-center flex-1 line-clamp-1 uppercase tracking-tight">
            Thiết Kế Cốc
          </h1>

          {/* Right: My Design Button */}
          <div className="flex-shrink-0">
            <a
              href="/history"
              className="flex items-center gap-1.5 px-2.5 py-2 bg-black text-white hover:bg-gray-800 rounded-xl transition-all text-[10px] sm:text-xs font-bold shadow-md active:scale-95"
            >
              <Clock className="w-3.5 h-3.5" />
              <span>My design</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden relative bg-gray-50">
        <ImageProcessingOverlay
          isLoading={loading}
          message={loadingMessage}
          progress={loadingProgress}
        />
        <SinglePageLayout
          designOptions={designOptions}
          setDesignOptions={setDesignOptions}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          templates={templates}
          onConfirm={confirmDesign}
          loading={loading}
          canConfirm={canConfirm()}
        />
      </main>
    </div>
  );
};

export default CupCustomizerPage;
